import * as React from "react";
import { cn } from "@/lib/cn";
import { usePrefersReducedMotion, useInView } from "@/lib/motion";

/**
 * Fades and lifts its children once, when they first scroll into view.
 *
 * Ten pixels and 420ms. Deliberately small: the job is to make a long page feel
 * like it is being read rather than scrolled past, and anything further than
 * that starts to feel like the content is arriving from somewhere else.
 *
 * `delay` staggers a row of cards. Keep it under ~120ms per step - beyond that
 * the last card in a row is still arriving after the reader has finished the
 * first one, which reads as a slow page rather than a considered one.
 */
export function Reveal({
  children,
  delay = 0,
  as = "div",
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  as?: "div" | "li" | "section";
  className?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLElement>();

  // Widened on purpose. A `"div" | "li" | "section"` union makes TypeScript
  // intersect all three elements' prop types, and no single ref satisfies
  // `RefObject<HTMLDivElement> & RefObject<HTMLLIElement>` - the alternative is
  // a generic polymorphic component, which is a great deal of type machinery for
  // a wrapper that only ever needs a ref and a class.
  const Tag = as as React.ElementType;

  return (
    <Tag
      ref={ref}
      className={cn(
        !reduced && "transition-[opacity,transform] duration-(--duration-slow) ease-out",
        !reduced && !inView && "translate-y-2.5 opacity-0",
        className
      )}
      style={reduced ? undefined : { transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}
